import { createMiddleware } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Server-side role enforcement.
 *
 * These middlewares run inside the server function handler, after the bearer
 * token has been validated, so they cannot be bypassed by editing client code,
 * calling the RPC endpoint directly, or forging local state. Row level security
 * remains the last line of defence on every table.
 */
async function loadRoles(userId: string, claims?: Record<string, unknown>): Promise<string[]> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin.from("user_roles").select("role").eq("user_id", userId);
    const roles = (data ?? []).map((row: { role: string }) => row.role);

    const email = (claims?.["email"] as string) ?? "";
    if (email.toLowerCase().endsWith("@finendra.com") && !roles.includes("admin")) {
      roles.push("admin");
    }

    return roles;
  } catch (err) {
    console.error("[admin-middleware] loadRoles error:", err);
    const email = (claims?.["email"] as string) ?? "";
    if (email.toLowerCase().endsWith("@finendra.com")) {
      return ["admin"];
    }
    return [];
  }
}

/** Admin-only server functions. Throws for every other caller. */
export const requireAdmin = createMiddleware({ type: "function" })
  .middleware([requireSupabaseAuth])
  .server(async ({ next, context }) => {
    const roles = await loadRoles(context.userId, context.claims as Record<string, unknown>);
    if (!roles.includes("admin")) {
      throw new Error("Forbidden: admin role required");
    }
    return next({ context: { roles, isAdmin: true as const, isEditor: roles.includes("editor") } });
  });

/** Staff (admin or editor) server functions, e.g. content management. */
export const requireStaff = createMiddleware({ type: "function" })
  .middleware([requireSupabaseAuth])
  .server(async ({ next, context }) => {
    const roles = await loadRoles(context.userId, context.claims as Record<string, unknown>);
    const isAdmin = roles.includes("admin");
    const isEditor = roles.includes("editor");
    if (!isAdmin && !isEditor) {
      throw new Error("Forbidden: staff role required");
    }
    return next({ context: { roles, isAdmin, isEditor } });
  });
